import { DeeplinkAction } from 'components-machine';
import React, { ReactNode, useContext } from 'react';
import { PollContext } from '../../store/withContext';

export type ICStateActions = {
  onChange: (
    questionId: string,
  ) => ({ answeredIds, answersOrder }: { answeredIds: string[]; answersOrder?: number[] }) => void;
  onNext: () => void;
  onOthersChange: (questionId: string) => ({ value }: { value: string }) => void;
  onMediaProgressChange: (questionId: string) => ({ progress }: { progress: number }) => void;
  onFileUpload: (questionId: string) => (fileName: string, path: string | null) => void;
  onAnswer: () => void;
  onTryAnswer: () => void;
  onBack: () => void;
  onSkip: () => void;
  onClose: () => void;
  onWelcomeClick: () => void;
  onFinalClick: (finalId: string) => void;
  onLinkClick: (external_url: string) => void;
  onStartGlobalTimer: () => void;
  onQuestionFocus: () => void;
  invokeDeeplink: (deeplink: DeeplinkAction['deeplink']) => Promise<unknown>;
};
const ICStateActionsContext = React.createContext<ICStateActions | undefined>(undefined);

export function ICStateActionsProvider({ children }: { children: ReactNode }) {
  const { interpreter } = useContext(PollContext);

  return (
    <ICStateActionsContext.Provider
      value={{
        invokeDeeplink: deeplink => {
          return new Promise<void>(resolve => {
            interpreter.send({
              type: 'invokeDeeplink',
              deeplink: {
                ...deeplink,
                onResolve: () => {
                  deeplink.onResolve?.();
                  resolve();
                },
              },
            });
          });
        },
        onStartGlobalTimer: () => {
          interpreter.send({ type: 'globalTimerStart' });
        },
        onChange:
          (questionId: string) =>
          ({ answeredIds, answersOrder }: { answeredIds: (string | number)[]; answersOrder?: number[] }) => {
            interpreter.send({
              type: 'change',
              questionId,
              answeredIds: answeredIds.map(id => id.toString()),
              answersOrder,
            });
          },
        onNext: () => {
          interpreter.send({ type: 'next' });
        },
        onOthersChange:
          (questionId: string) =>
          ({ value }: { value: string }) => {
            interpreter.send({ type: 'othersChange', questionId, value });
          },
        onMediaProgressChange:
          (questionId: string) =>
          ({ progress }) => {
            interpreter.send({ type: 'mediaProgressChange', questionId, progress });
          },
        onFileUpload: (questionId: string) => (fileName: string, path: string | null) => {
          interpreter.send({ type: 'fileUpload', questionId, fileName, path });
        },
        onAnswer: () => {
          interpreter.send({ type: 'answer' });
        },
        onTryAnswer: () => {
          interpreter.send({ type: 'tryAnswer' });
        },
        onWelcomeClick: () => {
          interpreter.send({ type: 'welcomeClick' });
        },
        onFinalClick: finalId => {
          interpreter.send({ type: 'finalClick', finalId });
        },
        onLinkClick: external_url => {
          interpreter.send({ type: 'linkClick', external_url });
        },
        onBack: () => {
          interpreter.send({ type: 'back' });
        },
        onSkip: () => {
          interpreter.send({ type: 'skip' });
        },
        onClose: () => {
          interpreter.send({ type: 'close' });
        },
        onQuestionFocus: () => {},
      }}
    >
      {children}
    </ICStateActionsContext.Provider>
  );
}
export function useICStateActions() {
  const value = useContext(ICStateActionsContext);
  if (!value) throw new Error('ICState context is required!');
  return value;
}
